// 🔧 app.js
// ✅ Purpose: Main routing file for the app
// ✅ Features:
// - Public routes (Login/Register/Reset) with no nav
// - Protected routes (admin/voter) wrapped in NavWrapper and WithNav
// - Uses RoleGuard to restrict access
// - All routes use lowercase for URL consistency
// - Redirects broken top-level slugs to /dashboard

import { useEffect } from "react";
import RequireValidEvent from "./components/RequireValidEvent";
import { initAnalytics, trackPage } from "./utils/analytics";
// Admin Only - Migration Script
//import FillDummyData from "./db-updates/admin_fill_dummy_data";
import ScrollToTop from "./components/scroll_to_top";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Admin Only
import AdminLogs from "./pages/admin/logs";
import AdminEventEdit from "./pages/admin/event";
import AdminEventsList from "./pages/admin/events_list";
import PendingEvents from "./pages/admin/pending_event";
import PendingEventsList from "./pages/admin/pending_events_list";
import CommunicationDashboard from "./pages/admin/communication_dashboard";
import CommunicationStudio from "./pages/admin/communication_studio";
import OrganizationHealth from "./pages/admin/organizers/organization_health";

// Public Pages
import LandingHome from "./pages/public/landing_home";
import Login from "./pages/public/login";
import Register from "./pages/public/register";
import VerifyEmail from "./pages/public/verify_email";
import EmailAction from "./pages/public/email_action";
import ResendVerification from "./pages/public/resend_verification";
import Logout from "./pages/public/logout";
import Reset from "./pages/public/reset";
import Volunteering from "./pages/public/volunteering";
import VolunteerOpportunities from "./pages/public/volunteer_opportunities";
import VolunteerVerification from "./pages/public/volunteer_verification";
import EventHome from "./pages/public/event_home";
import NotFound from "./pages/not_found";
import Terms from "./pages/public/terms";
import SmsAlerts from "./pages/public/sms_alerts";
import PublicEntry from "./pages/public/public_entry";
import ManualVote from "./pages/public/manual_vote";
import RequestEvent from "./pages/public/request_event";
import CarShows from "./pages/marketing/car_shows";
import ArtShows from "./pages/marketing/art_shows";
import PetShows from "./pages/marketing/pet_shows";
import SchoolElections from "./pages/marketing/school_elections";
import SilentAuctions from "./pages/marketing/silent_auctions";
import Sponsors from "./pages/public/sponsors";
import MySponsors from "./pages/public/my_sponsors";
import SponsorVerification from "./pages/public/sponsor_verification";
import VendorVerification from "./pages/public/vendor_verification";
import Entries from "./pages/public/entries";
import EntryView from "./pages/public/entry_view";
import Contact from "./pages/marketing/contact";
import Vendors from "./pages/public/vendors";
import VendorView from "./pages/public/vendor_view";
import BuyTickets from "./pages/public/buy_tickets";
import MyOrders from "./pages/public/my_orders";
import RequestEventSuccess from "./pages/public/request_event_success";
import BecomeSponsor from "./pages/public/become_sponsor";
import BecomeVendor from "./pages/public/become_vendor";
import Store from "./pages/public/store";
import StoreProductView from "./pages/public/store_product_view";
import StoreCart from "./pages/public/store_cart";
import StoreSuccess from "./pages/public/store_success";
import Passport from "./pages/public/passport";
import PassportStop from "./pages/public/passport_stop";
import PassportAchievements from "./pages/public/passport_achievements";
import PassportLeaderboard from "./pages/public/passport_leaderboard";
import EventContextProvider from "./context/event_context_provider";

// ✅ Public Help / Guide Pages
import Help from "./pages/public/help";
import Guides from "./pages/public/guides";
import GettingStartedGuide from "./pages/public/guides/getting_started";
import ConfigureVotingGuide from "./pages/public/guides/configure_voting";
import CategoriesAndEntriesGuide from "./pages/public/guides/categories_and_entries";
import QrCodesGuide from "./pages/public/guides/qr_codes";
import RunningEventDayGuide from "./pages/public/guides/running_event_day";
import SponsorsGuide from "./pages/public/guides/sponsors";
import VolunteersGuide from "./pages/public/guides/volunteers";
import SilentAuctionsGuide from "./pages/public/guides/silent_auctions";
import SecurityAndTrustGuide from "./pages/public/guides/security_and_trust";
import CarShowPlaybook from "./pages/public/guides/car_show_playbook";
import ArtShowPlaybook from "./pages/public/guides/art_show_playbook";
import PetShowPlaybook from "./pages/public/guides/pet_show_playbook";
import SchoolElectionPlaybook from "./pages/public/guides/school_election_playbook";

// Marketing Pages
import Solutions from "./pages/marketing/solutions";
import QrCodeVoting from "./pages/marketing/qr_code_voting";
import PeoplesChoiceVoting from "./pages/marketing/peoples_choice_voting";
import RankedChoiceVoting from "./pages/marketing/ranked_choice_voting";
import JudgedScorecards from "./pages/marketing/judged_scorecards";
import VolunteerManagement from "./pages/marketing/volunteer_management";
import SponsorManagement from "./pages/marketing/sponsor_management";
import HowItWorks from "./pages/marketing/how_it_works";
import About from "./pages/marketing/about";
import Privacy from "./pages/marketing/privacy";

// Vote Pages
import Vote from "./pages/public/vote";
import Ballot from "./pages/public/ballot";
import Leaderboard from "./pages/public/leaderboard";
import SponsorView from "./pages/public/sponsor_view";

// Admin + Manager Pages
import Dashboard from "./pages/public/dashboard";
import AdminUsers from "./pages/admin/users";
import SponsorForm from "./pages/admin/sponsor";
import SponsorList from "./pages/admin/sponsors_list";
import EntryReg from "./pages/admin/entry_reg";
import Entry from "./pages/admin/entry";
import QrPrint from "./pages/admin/qr_print";
import AdminAudit from "./pages/admin/audit";
import AdminCategories from "./pages/admin/categories";
import VoteAudit from "./pages/admin/vote_audit";
import Volunteers from "./pages/admin/volunteers";
import AdminUserProfile from "./pages/admin/user_profile";
import VolunteersNext from "./pages/admin/volunteers_next";
import ManagerAccept from "./manager_accept";
import PublicLinks from "./pages/admin/public_links";
import LeaderboardPrint from "./pages/admin/leaderboard_print";
import JudgeResults from "./pages/admin/judge_results";
import AuctionQrPrint from "./pages/admin/auction_qr_print";
import Settings from "./pages/admin/settings";
import Content from "./pages/admin/content";
import Categories from "./pages/admin/categories";
import Managers from "./pages/admin/managers";
import Scoring from "./pages/admin/scoring";
import VolunteerSettings from "./pages/admin/volunteer_settings";
import SponsorsSettings from "./pages/admin/sponsors_settings";
import Images from "./pages/admin/images";
import QrSettings from "./pages/admin/qr_settings";
import PendingEntries from "./pages/admin/pending_entries";
import SeoQueue from "./pages/admin/seo_queue";
import SettingsMap from "./pages/admin/settings_map";
import Scheduler from "./pages/admin/scheduler";
import VendorsSettings from "./pages/admin/vendors_settings";
import VendorsList from "./pages/admin/vendors_list";
import VendorForm from "./pages/admin/vendor";
import Tickets from "./pages/admin/tickets";
import StoreProducts from "./pages/admin/store_products";
import StoreVariants from "./pages/admin/store_variants";
import StoreSettings from "./pages/admin/store_settings";
import StoreOrders from "./pages/admin/store_orders";
import EventPackage from "./pages/admin/event_package";
import EventSettings from "./pages/admin/event_settings";
import Engagement from "./pages/admin/engagement";
import EngagementActivities from "./pages/admin/engagement_activities";
import SalesDashboard from "./pages/admin/sales_dashboard";
import OutreachQueue from "./pages/admin/outreach_queue";
import AiMissionControl from "./pages/admin/ai_mission_control";
import FollowUpQueue from "./pages/admin/follow_up_queue";
import ResearchQueue from "./pages/admin/research_queue";
import DraftQueue from "./pages/admin/draft_queue";
import AgentRuns from "./pages/admin/agent_runs";
import AgentSettings from "./pages/admin/agent_settings";
import AutomationScheduler from "./pages/admin/automation_scheduler";
import EngagementFunnel from "./pages/admin/engagement_funnel";
import EventIntelligence from "./pages/admin/event_intelligence";
import DemoBuilder from "./pages/admin/demo_builder";
import PromotionDashboard from "./pages/admin/promotions/promotion_dashboard";
import PromotionContentStudio from "./pages/admin/promotions/promotion_content_studio";
import PromotionScheduling from "./pages/admin/promotions/promotion_scheduling";
import PromotionSocialConnections from "./pages/admin/promotions/promotion_social_connections";
import SocialConnections from "./pages/admin/social_connections";
import EventPromotionGuard from "./promotion/components/event_promotion_guard";
import Organizers from "./pages/admin/organizers";

// Auction
import Auction from "./pages/public/auction";
import Auctions from "./pages/admin/auctions";
import AuctionResults from "./pages/admin/auction_results";
import AuctionItem from "./pages/public/auction_item";
import AuctionQrPrintAll from "./pages/admin/auction_qr_print_all";

// Layout and Access Control
import RoleGuard from "./role_guard";
import PublicRoute from "./public_route";
import NavWrapper from "./layouts/nav_wrapper";
import AppLayout from "./layouts/app_layout";

function App() {
  const location = useLocation();
  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPage(location.pathname + location.search);

    // ✅ Track SPA route changes for Meta
  }, [location]);

  return (
    
    <div className="app">
      <ScrollToTop />
      <Routes>

        {/*Admin Only - Migration Script */}
        {/*<Route path="/admin/fill-dummy-data" element={<FillDummyData />} /> */}

        {/* 🚧 Add path here for links that if fail need a redirect. Mostly for QR or manual Vote/Submit links */}
        <Route path="/vote/:id" element={<Navigate to="/dashboard" replace />} />

        {/* ⛵ Main App Layout */}
        <Route element={<NavWrapper />}>

          {/* Public Routes */}
          <Route path="/" element={<LandingHome />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute allowGuests={true}><Register /></PublicRoute>} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/reset" element={<Reset />} />
          <Route path="/manager_accept/:eventslug" element={<ManagerAccept />} />
          <Route path="/verify_email" element={<EmailAction />} />
          <Route path="/email_action" element={<VerifyEmail />} />
          <Route path="/resend_verification" element={<ResendVerification />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/sms-alerts" element={<SmsAlerts />} />
          <Route path="/request_event" element={<RequestEvent />} />
          <Route path="request_event_success" element={<RequestEventSuccess />} />
          <Route path="/car-shows" element={<CarShows />} />
          <Route path="/art-shows" element={<ArtShows />} />
          <Route path="/pet-shows" element={<PetShows />} />
          <Route path="/school-elections" element={<SchoolElections />} />
          <Route path="/silent-auctions" element={<SilentAuctions />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/qr-code-voting" element={<QrCodeVoting />} />
          <Route path="/peoples-choice-voting" element={<PeoplesChoiceVoting />} />
          <Route path="/ranked-choice-voting" element={<RankedChoiceVoting />} />
          <Route path="/judged-scorecards" element={<JudgedScorecards />} />
          <Route path="/volunteer-management" element={<VolunteerManagement />} />
          <Route path="/sponsor-management" element={<SponsorManagement />} />
          <Route path="/how-it-works" element={<HowItWorks />}/>
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/buy_tickets" element={<BuyTickets />} />

          {/* ✅ Public Help / Guide Routes */}
          <Route path="/help" element={<Help />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/getting-started" element={<GettingStartedGuide />} />
          <Route path="/guides/configure-voting" element={<ConfigureVotingGuide />} />
          <Route path="/guides/categories-and-entries" element={<CategoriesAndEntriesGuide />} />
          <Route path="/guides/qr-codes" element={<QrCodesGuide />} />
          <Route path="/guides/running-event-day" element={<RunningEventDayGuide />} />
          <Route path="/guides/sponsors" element={<SponsorsGuide />} />
          <Route path="/guides/volunteers" element={<VolunteersGuide />} />
          <Route path="/guides/silent-auctions" element={<SilentAuctionsGuide />} />
          <Route path="/guides/security-and-trust" element={<SecurityAndTrustGuide />} />
          <Route path="/guides/car-show-playbook" element={<CarShowPlaybook />} />
          <Route path="/guides/art_show_playbook" element={<ArtShowPlaybook />} />
          <Route path="/guides/pet-show-playbook" element={<PetShowPlaybook />} />
          <Route path="/guides/school-election-playbook" element={<SchoolElectionPlaybook />} />

          {/* Dashboard (default destination if no slug) */}
          <Route path="/dashboard" element={<RoleGuard allowedRoles={["voter", "admin", "manager"]}><Dashboard /></RoleGuard>} />

          {/* Admin-only */}
          <Route path="/logs" element={<RoleGuard allowedRoles={["admin"]}><AdminLogs /></RoleGuard>} />
          <Route path="/prospects" element={<RoleGuard allowedRoles={["admin"]}><SalesDashboard /></RoleGuard>} />
          <Route path="/seo_queue" element={<RoleGuard allowedRoles={["admin"]}><SeoQueue /></RoleGuard>} />
          <Route path="/event" element={<RoleGuard allowedRoles={["admin"]}><AdminEventEdit /></RoleGuard>} />
          <Route path="/event/:editId" element={<RoleGuard allowedRoles={["admin"]}><AdminEventEdit /></RoleGuard>} />
          <Route path="/events_list" element={<RoleGuard allowedRoles={["admin"]}><AdminEventsList /></RoleGuard>} />
          <Route path="/pending_event/:id" element={<RoleGuard allowedRoles={["admin"]}><PendingEvents /></RoleGuard>} />
          <Route path="/pending_events_list" element={<RoleGuard allowedRoles={["admin"]}><PendingEventsList /></RoleGuard>} />
          <Route path="/sales_dashboard" element={<RoleGuard allowedRoles={["admin"]}><SalesDashboard /></RoleGuard>} />
          <Route path="/prospect-sources" element={<RoleGuard allowedRoles={["admin"]}><SalesDashboard /></RoleGuard>} />
          <Route path="/outreach-queue" element={<RoleGuard allowedRoles={["admin", "manager"]}><OutreachQueue /></RoleGuard>} />
          <Route path="/ai-mission-control" element={<RoleGuard allowedRoles={["admin", "manager"]}><AiMissionControl  /></RoleGuard>} />
          <Route path="/follow-up-queue" element={<RoleGuard allowedRoles={["admin", "manager"]}><FollowUpQueue /></RoleGuard>} />
          <Route path="/research-queue" element={<RoleGuard allowedRoles={["admin", "manager"]}><ResearchQueue /></RoleGuard>} />
          <Route path="/draft-queue" element={<RoleGuard allowedRoles={["admin", "manager"]}><DraftQueue /></RoleGuard>} />
          <Route path="/agent-runs" element={<RoleGuard allowedRoles={["admin", "manager"]}><AgentRuns /></RoleGuard>} />
          <Route path="/agent-settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><AgentSettings /></RoleGuard>} />
          <Route path="/automation-scheduler" element={<RoleGuard allowedRoles={["admin", "manager"]}><AutomationScheduler  /></RoleGuard>} />
          <Route path="/demo_builder" element={<RoleGuard allowedRoles={["admin"]}><DemoBuilder /></RoleGuard>} />

          {/* Admin Only  */}
          <Route path="/promotion_dashboard" element={<RoleGuard allowedRoles={["admin"]}><PromotionDashboard /></RoleGuard>} />
          <Route path="/promotion_ideas" element={<RoleGuard allowedRoles={["admin"]}><Navigate to="/promotion_dashboard" replace /></RoleGuard>} />
          <Route path="/promotion_studio" element={<RoleGuard allowedRoles={["admin"]}><PromotionContentStudio /></RoleGuard>} />
          <Route path="/promotion_scheduling" element={<RoleGuard allowedRoles={["admin"]}><PromotionScheduling /></RoleGuard>} />
          <Route path="/promotion_social_connections" element={<RoleGuard allowedRoles={["admin"]}><PromotionSocialConnections /></RoleGuard>} />
          <Route path="/communication_dashboard" element={<RoleGuard allowedRoles={["admin"]}><CommunicationDashboard  /></RoleGuard>} />
          <Route path="/communication_studio" element={<RoleGuard allowedRoles={["admin"]}><CommunicationStudio  /></RoleGuard>} />
          <Route path="/organizers" element={<RoleGuard allowedRoles={["admin"]}><Organizers  /></RoleGuard>} />
          <Route path="/organizers/:organizationId/health" element={<RoleGuard allowedRoles={["admin"]}><OrganizationHealth /></RoleGuard>} />
          

        </Route>

        {/* 🔁 Event-scoped routes (voter/admin/manager) */}
        <Route
          path=":eventslug"
          element={
            <EventContextProvider>
              <RequireValidEvent>
                <AppLayout />
              </RequireValidEvent>
            </EventContextProvider>
          }
        >
          <Route path="leaderboard_print" element={<RoleGuard allowedRoles={["admin", "manager"]}><LeaderboardPrint /></RoleGuard>} />
          <Route path="judge_results" element={<RoleGuard allowedRoles={["judge", "admin", "manager"]}><JudgeResults /></RoleGuard>} />
          <Route path="public_links" element={<RoleGuard allowedRoles={["admin", "manager"]}><PublicLinks /></RoleGuard>} />
          <Route
            path="sponsors/:sponsorSlug"
            element={<SponsorView />}
          />
          <Route path="event" element={<EventHome />} />
          <Route path="" element={<EventHome />} />
          <Route path="public_entry" element={<PublicEntry />} />
          <Route path="volunteering" element={<Volunteering />} />
          <Route path="volunteer_verification/:activityId/:slotId" element={<VolunteerVerification />} />
          <Route path="sponsors" element={<Sponsors />} />
          <Route path="my_sponsors" element={<MySponsors />} />
          <Route path="sponsor_verification/:sponsorId" element={<SponsorVerification />} />
          <Route path="vendor_verification/:vendorId" element={<VendorVerification />} />
          <Route path="entries" element={<Entries />} />
          <Route path="entries/:entryId" element={<EntryView />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="vendors/:vendorSlug" element={<VendorView />} />
          <Route path="buy_tickets" element={<BuyTickets />} />
          <Route path="become_sponsor" element={<BecomeSponsor />} />
          <Route path="become_vendor" element={<BecomeVendor />} />
          <Route path="store" element={<Store />} />
          <Route path="volunteer_opportunities" element={<VolunteerOpportunities />} />
          <Route path="store/:productSlug" element={<StoreProductView />} />
          <Route path="cart" element={<StoreCart />} />
          <Route path="store_success" element={<StoreSuccess />} />
          <Route path="passport" element={<Passport />} />
          <Route path="passport/stop/:activityId" element={<PassportStop />} />
          <Route path="passport/achievements" element={<PassportAchievements />} />
          <Route path="passport/leaderboard" element={<PassportLeaderboard />} />

          <Route path="event_intelligence" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventIntelligence  /></RoleGuard>} />
          <Route path="event_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventSettings /></RoleGuard>} />
          <Route path="event_package" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPackage /></RoleGuard>} />
          <Route path="store_orders" element={<RoleGuard allowedRoles={["admin", "manager"]}><StoreOrders /></RoleGuard>} />
          <Route path="store_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><StoreSettings /></RoleGuard>} />
          <Route path="store_variants/:productId" element={<RoleGuard allowedRoles={["admin", "manager"]}><StoreVariants /></RoleGuard>} />
          <Route path="vote/:id" element={<RoleGuard allowedRoles={["voter", "admin"]} allowGuests={true}><Vote /></RoleGuard>} />
          <Route path="ballot" element={<RoleGuard allowedRoles={["voter", "admin"]}><Ballot /></RoleGuard>} />
          <Route path="leaderboard" element={<RoleGuard allowedRoles={["voter", "judge", "manager", "admin"]}><Leaderboard /></RoleGuard>} />
          <Route path="store_products" element={<RoleGuard allowedRoles={["admin", "manager"]}><StoreProducts /></RoleGuard>} />
          <Route path="scheduler" element={<RoleGuard allowedRoles={["admin", "manager"]}><Scheduler /></RoleGuard>} />
          <Route path="sponsor" element={<RoleGuard allowedRoles={["admin", "manager"]}><SponsorForm /></RoleGuard>} />
          <Route path="sponsor/:id" element={<RoleGuard allowedRoles={["admin", "manager"]}><SponsorForm /></RoleGuard>} />
          <Route path="sponsors_list" element={<RoleGuard allowedRoles={["admin", "manager"]}><SponsorList /></RoleGuard>} />
          <Route path="users" element={<RoleGuard allowedRoles={["admin", "manager"]}><AdminUsers /></RoleGuard>} />
          <Route path="entry" element={<RoleGuard allowedRoles={["admin", "manager"]}><Entry /></RoleGuard>} />
          <Route path="entry_reg" element={<RoleGuard allowedRoles={["admin", "manager"]}><EntryReg /></RoleGuard>} />
          <Route path="entry_reg/:id" element={<RoleGuard allowedRoles={["admin", "manager"]}><EntryReg /></RoleGuard>} />
          <Route
            path="qr_print/:carId"
            element={
              <RoleGuard allowedRoles={["admin", "manager"]}>
                <QrPrint />
              </RoleGuard>
            }
          />

          <Route
            path="qr_print"
            element={
              <RoleGuard allowedRoles={["admin", "manager"]}>
                <QrPrint />
              </RoleGuard>
            }
          />

          {/* =========================================================
              Legacy Print-All URL
              Purpose:
              - Preserve existing links/bookmarks to qr_print_all
              - Route them through the new unified QR Print page
              - Gives grouped printing access to all print-size options
              ========================================================= */}

          <Route
            path="qr_print_all"
            element={
              <RoleGuard allowedRoles={["admin", "manager"]}>
                <QrPrint />
              </RoleGuard>
            }
          />
          <Route path="audit" element={<RoleGuard allowedRoles={["admin", "manager"]}><AdminAudit /></RoleGuard>} />
          <Route path="categories" element={<RoleGuard allowedRoles={["admin", "manager"]}><AdminCategories /></RoleGuard>} />
          <Route path="vote_audit/:cid" element={<RoleGuard allowedRoles={["admin", "manager"]}><VoteAudit /></RoleGuard>} />
          <Route path="user_profile/:userId" element={<RoleGuard allowedRoles={["admin", "manager"]}><AdminUserProfile /></RoleGuard>} />
          <Route path="volunteers" element={<RoleGuard allowedRoles={["admin", "manager"]}><Volunteers /></RoleGuard>} />
          <Route path="volunteers_next" element={<RoleGuard allowedRoles={["admin", "manager"]}><VolunteersNext /></RoleGuard>} />
          <Route path="manual_vote" element={<RoleGuard allowedRoles={["admin", "manager"]}><ManualVote /></RoleGuard>} />
          <Route path="settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><Settings /></RoleGuard>} />
          <Route path="content" element={<RoleGuard allowedRoles={["admin", "manager"]}><Content /></RoleGuard>} />
          <Route path="categories" element={<RoleGuard allowedRoles={["admin", "manager"]}><Categories /></RoleGuard>} />
          <Route path="managers" element={<RoleGuard allowedRoles={["admin", "manager"]}><Managers /></RoleGuard>} />
          <Route path="scoring" element={<RoleGuard allowedRoles={["admin", "manager"]}><Scoring /></RoleGuard>} />
          <Route path="volunteer_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><VolunteerSettings /></RoleGuard>} />
          <Route path="sponsors_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><SponsorsSettings /></RoleGuard>} />
          <Route path="images" element={<RoleGuard allowedRoles={["admin", "manager"]}><Images /></RoleGuard>} />
          <Route path="qr_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><QrSettings /></RoleGuard>} />  
          <Route path="pending_entries" element={<RoleGuard allowedRoles={["admin", "manager"]}><PendingEntries /></RoleGuard>} />  
          <Route path="map_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><SettingsMap /></RoleGuard>} />  
          <Route path="vendors_settings" element={<RoleGuard allowedRoles={["admin", "manager"]}><VendorsSettings /></RoleGuard>} />  
          <Route path="vendors_list" element={<RoleGuard allowedRoles={["admin", "manager"]}><VendorsList /></RoleGuard>} />  
          <Route path="vendor" element={<RoleGuard allowedRoles={["admin", "manager"]}><VendorForm /></RoleGuard>} />
          <Route path="vendor/:id" element={<RoleGuard allowedRoles={["admin", "manager"]}><VendorForm /></RoleGuard>} />
          <Route path="payment_settings" element={<Navigate to="../store_settings" replace />} />
          <Route path="tickets" element={<RoleGuard allowedRoles={["admin", "manager"]}><Tickets /></RoleGuard>} />
          <Route path="orders" element={<Navigate to="../store_orders" replace />} />
          <Route path="engagement" element={<RoleGuard allowedRoles={["admin", "manager"]}><Engagement /></RoleGuard>} />
          <Route path="engagement_activities" element={<RoleGuard allowedRoles={["admin", "manager"]}><EngagementActivities /></RoleGuard>} />
          <Route path="engagement_funnel" element={<RoleGuard allowedRoles={["admin", "manager"]}><EngagementFunnel /></RoleGuard>} />

          {/* Promotion Engine (MVP2 - Event Scoped) */}
          <Route path="integrations" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><SocialConnections /></EventPromotionGuard></RoleGuard>} />
          <Route path="promotion_dashboard" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><PromotionDashboard /></EventPromotionGuard></RoleGuard>} />
          <Route path="promotion_ideas" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><Navigate to="../promotion_dashboard" replace /></EventPromotionGuard></RoleGuard>} />
          <Route path="promotion_studio" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><PromotionContentStudio /></EventPromotionGuard></RoleGuard>} />
          <Route path="promotion_scheduling" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><PromotionScheduling /></EventPromotionGuard></RoleGuard>} />
          <Route path="promotion_social_connections" element={<RoleGuard allowedRoles={["admin", "manager"]}><EventPromotionGuard><PromotionSocialConnections /></EventPromotionGuard></RoleGuard>} />

          {/* Auction */}
          <Route path="auctions" element={<RoleGuard allowedRoles={["admin", "manager"]}><Auctions /></RoleGuard>} />
          <Route path="auction" element={<Auction />} />
          <Route path="auction_results" element={<RoleGuard allowedRoles={["admin", "manager"]}><AuctionResults /></RoleGuard>} />
          <Route path="auction/:itemId" element={<AuctionItem />} />
          <Route path="auction_qr_print/:itemId" element={<RoleGuard allowedRoles={["admin", "manager"]}><AuctionQrPrint  /></RoleGuard>} />
          <Route path="auction_qr_print_all" element={<RoleGuard allowedRoles={["admin", "manager"]}><AuctionQrPrintAll /></RoleGuard>} />

          <Route path="my_orders" element={<RoleGuard allowedRoles={["voter", "admin", "manager"]}><MyOrders /></RoleGuard>} />

        </Route>

        {/* Catch all not found (must be within NavWrapper) */}
        <Route path="*" element={<NotFound />} />
        <Route path="/not-found" element={<NotFound />} />

      </Routes>
    </div>
  );
}

export default App;
